import { formatCpfProgressively, onlyDigits } from './credential-identifiers';

/**
 * CPF (Cadastro de Pessoas Físicas) Validator
 * Brazilian individual taxpayer registry number validation utility
 */
export class CpfValidator {
  /**
   * Validates a CPF number
   * @param cpf - The CPF string (with or without formatting)
   * @returns true if valid, false otherwise
   */
  static validate(cpf: string): boolean {
    // Remove all non-digit characters
    const cleaned = onlyDigits(cpf);

    // CPF must have exactly 11 digits
    if (cleaned.length !== 11) {
      return false;
    }

    // Check for known invalid patterns (all same digits)
    if (/^(\d)\1{10}$/.test(cleaned)) {
      return false;
    }

    // Validate check digits
    return this.validateCheckDigits(cleaned);
  }

  /**
   * Formats a CPF string to the standard format: ###.###.###-##
   * @param cpf - The CPF string (digits only or partially formatted)
   * @returns Formatted CPF string
   */
  static format(cpf: string): string {
    return formatCpfProgressively(cpf);
  }

  /**
   * Removes formatting from a CPF string
   * @param cpf - The formatted CPF string
   * @returns CPF with only digits
   */
  static unformat(cpf: string): string {
    return onlyDigits(cpf);
  }

  /**
   * Masks a CPF for display (shows only last 2 digits)
   * @param cpf - The CPF string
   * @returns Masked CPF: ***.***.**-##
   */
  static mask(cpf: string): string {
    const cleaned = onlyDigits(cpf);

    if (cleaned.length < 11) {
      return cpf;
    }

    return `***.***.**${cleaned.slice(8, 9)}-${cleaned.slice(9, 11)}`;
  }

  /**
   * Validates the CPF check digits using the official algorithm
   * @param cpf - 11-digit CPF string
   * @returns true if check digits are valid
   */
  private static validateCheckDigits(cpf: string): boolean {
    // Calculate first check digit
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i), 10) * (10 - i);
    }

    let remainder = sum % 11;
    const firstCheckDigit = remainder < 2 ? 0 : 11 - remainder;

    if (parseInt(cpf.charAt(9), 10) !== firstCheckDigit) {
      return false;
    }

    // Calculate second check digit
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i), 10) * (11 - i);
    }

    remainder = sum % 11;
    const secondCheckDigit = remainder < 2 ? 0 : 11 - remainder;

    return parseInt(cpf.charAt(10), 10) === secondCheckDigit;
  }
}

